import { createAction, createSelector } from '@reduxjs/toolkit'
import isNull from 'lodash/isNull'
import { all, call, ForkEffect, put, SagaReturnType, select, takeEvery } from 'redux-saga/effects'

import {
    createMasterSetPlans,
    fetchAllProjectDocumentMappings,
    fetchProjectDocumentChunks,
} from '../../../api/projects-api'
import { DocumentMapping } from '../../../models/documentMapping'
import { IMasterSetPlanToPost, MASTER_SET_PLAN_ENUMS } from '../../../models/masterSetPlan'
import { Project } from '../../../models/project'
import { PLAN_VIEWS_ENUM } from '../../../shared/constants/plan-views'
import { RootState } from '../../../stores'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { MasterSetPlanRegion, Overlay } from '../../lib/toolBoxes/2D'
import { selectClassificationOverlaidChunkId, updateClassificationOverlaidChunkId } from '../../slices/2D'
import { updateToolbarMessage } from '../../slices/common'
import {
    initializeDocumentChunks,
    initializeProjectDocumentsFromChunks,
    selectDocumentChunksWBuildingID,
    updateDocumentMappings,
} from '../../slices/documents'
import { addMasterSetPlanOptionToStore, selectMasterSetPlanToCreate } from '../../slices/masterSetPlan'
import { Coordinates2D, GENERATING_CLASSIFICATION } from '../../types'

export const createMasterSetPlanAction = createAction<{
    masterSetPlanOptionType: MASTER_SET_PLAN_ENUMS
    flagText: string
}>('createMasterSetPlanAction')

export const selectProjectActiveChunkState = createSelector(
    ({ IMUP: { documents, project } }: RootState) => {
        return {
            activeDocumentChunkId: documents.activeDocumentChunkId,
            project: project.project,
        }
    },
    (state) => state
)

const calculateBoundingBoxForCoordinates = (coords: Coordinates2D): Coordinates2D => {
    const { minX, maxX, minY, maxY } = coords.reduce(
        (minMax, coord) => {
            if (coord[0] < minMax.minX) {
                minMax.minX = coord[0]
            }

            if (coord[0] > minMax.maxX) {
                minMax.maxX = coord[0]
            }

            if (coord[1] < minMax.minY) {
                minMax.minY = coord[1]
            }

            if (coord[1] > minMax.maxY) {
                minMax.maxY = coord[1]
            }

            return minMax
        },
        { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity }
    )

    return [
        [minX, minY],
        [minX, maxY],
        [maxX, maxY],
        [maxX, minY],
    ]
}

export function* handleCreateMasterSetPlanAction({ payload }: ReturnType<typeof createMasterSetPlanAction>) {
    const paperManager: PaperManager | null = yield call(managers.get2DManager)

    if (!paperManager) return

    try {
        yield put(updateToolbarMessage(GENERATING_CLASSIFICATION))
        const { activeDocumentChunkId, project }: { activeDocumentChunkId: number | null; project: Project | null } =
            yield select(selectProjectActiveChunkState)

        if (activeDocumentChunkId === null || project === null) return

        const classificationOverlaidChunkId: number | null = yield select(selectClassificationOverlaidChunkId)

        const [masterSetPlanRegionTool, overlayTool]: [MasterSetPlanRegion, Overlay] = yield call(
            paperManager.getTools,
            [MasterSetPlanRegion.NAME, Overlay.NAME]
        )

        const documentChunks: ReturnType<typeof selectDocumentChunksWBuildingID> = yield select(
            selectDocumentChunksWBuildingID
        )
        const buildingId = documentChunks?.find((d) => d.id === activeDocumentChunkId)?.buildingID

        const { masterSetPlanToCreate, masterSetPlanFormDataState }: ReturnType<typeof selectMasterSetPlanToCreate> =
            yield select(selectMasterSetPlanToCreate)

        const { name: selectedFormName, type: selectedFormType } = masterSetPlanFormDataState

        const documentToCropOptions: ReturnType<typeof overlayTool.extractCroppingAPIData> | undefined = !isNull(
            classificationOverlaidChunkId
        )
            ? yield call(overlayTool.extractCroppingAPIData.bind(overlayTool), classificationOverlaidChunkId)
            : undefined

        if (masterSetPlanToCreate.length && buildingId && selectedFormName && selectedFormType) {
            const masterSetOptionsToPost: IMasterSetPlanToPost = {
                plans: masterSetPlanToCreate.map((m) => {
                    return {
                        name: selectedFormName,
                        isOption: payload.masterSetPlanOptionType === MASTER_SET_PLAN_ENUMS.MASTER_SET_PLAN_OPTION,
                        scale: masterSetPlanFormDataState.scale,
                        type: selectedFormType,
                        floorLevel: masterSetPlanFormDataState.floorLevel,
                        // To allow for more than 4 pointed polygons
                        // to be created, limit to the bounding box
                        // of the coordinates created by the user
                        coordinates: calculateBoundingBoxForCoordinates(m.coordinates),
                        chunkToCropAndOverlay: documentToCropOptions,
                        flagText: payload.flagText.length > 0 ? payload.flagText : null,
                    }
                }),
                building_id: buildingId,
            }

            const responseMasterSetPlanOptions: DocumentMapping[] = yield call(
                createMasterSetPlans,
                project.id,
                activeDocumentChunkId,
                masterSetOptionsToPost
            )

            if (responseMasterSetPlanOptions) {
                yield put(
                    addMasterSetPlanOptionToStore(
                        responseMasterSetPlanOptions.map((mapping) => {
                            const mappingToCreateData = masterSetOptionsToPost.plans.find(
                                (mToCreate) =>
                                    mToCreate.name === mapping.page_name && mToCreate.isOption === mapping.is_option
                            )

                            return {
                                id: mapping.id,
                                document_chunk_id: mapping.document_chunk_id,
                                coordinates: calculateBoundingBoxForCoordinates(mappingToCreateData!.coordinates),
                                isOption: mapping.is_option,
                                scale: mappingToCreateData!.scale,
                                name: mapping.page_name ?? '',
                                type: mapping.type ?? PLAN_VIEWS_ENUM.FLOOR,
                            }
                        })
                    )
                )

                // draw new master set plan options
                yield all(
                    responseMasterSetPlanOptions.map((msOption) => {
                        const mappingToCreateData = masterSetOptionsToPost.plans.find(
                            (mToCreate) =>
                                mToCreate.name === msOption.page_name && mToCreate.isOption === msOption.is_option
                        )

                        return call(
                            masterSetPlanRegionTool.renderBaseHomeOptionPageRegion,
                            calculateBoundingBoxForCoordinates(mappingToCreateData!.coordinates),
                            msOption.id,
                            msOption.is_option
                        )
                    })
                )

                const mappings: DocumentMapping[] = yield call(fetchAllProjectDocumentMappings, project.id)

                const chunks: SagaReturnType<typeof fetchProjectDocumentChunks> = yield call(
                    fetchProjectDocumentChunks,
                    project.id
                )

                yield put(updateDocumentMappings(mappings))

                yield put(initializeDocumentChunks(chunks))

                yield put(initializeProjectDocumentsFromChunks(chunks))
                yield put(updateClassificationOverlaidChunkId(null))
            }
        }
    } catch (error) {
        yield call(console.error, error)
    }

    yield put(updateToolbarMessage(null))
}

export function* watchForCreateMasterSetPlanAction(): Generator<ForkEffect<never>, void, unknown> {
    yield takeEvery(createMasterSetPlanAction.type, handleCreateMasterSetPlanAction)
}
